import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Button from '../../../shared/components/ui/Button';
import { fetchJobById } from '../services/jobService';
import { saveJob, unsaveJob, applyJob } from '../../../services/api';
import { formatPostedAt } from '../utils/jobHelpers';
import { getAuth } from '../../auth/utils/authStorage';
import { getCallLink, getWhatsAppLink } from '../../../utils/phoneLinks';
import { getMediaUrl } from '../../../utils/mediaUrl';
import CardIcon from '../../../shared/components/icons/CardIcon';

const formatDetailsDate = (timestamp) => (
  timestamp
    ? new Date(timestamp).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : 'N/A'
);

function JobDetailsPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [job, setJob] = useState(location.state?.job || null);
  const [isSaved, setIsSaved] = useState(location.state?.job?.isSaved || false);
  const [hasApplied, setHasApplied] = useState(location.state?.job?.hasApplied || false);
  const [loading, setLoading] = useState(!location.state?.job);
  const [error, setError] = useState(null);
  const [avatarVisible, setAvatarVisible] = useState(true);

  const auth = getAuth();
  const applicationView = Boolean(location.state?.applicationView);
  const savedView = Boolean(location.state?.savedView);
  const isClientView = location.pathname.startsWith('/client');
  const applicationStatus = (location.state?.job?.applicationStatus || job?.applicationStatus || 'pending').toLowerCase();
  const employer = job?.user || job?.client || job?.employer || job?.company || {};
  const profilePhoto = getMediaUrl(employer.profilePhoto || employer.profile?.profilePhoto || employer.avatarUrl || employer.photoUrl);
  const hasProfilePhoto = Boolean(profilePhoto) && avatarVisible;
  const backPath = applicationView
    ? '/worker/applications'
    : savedView
      ? '/worker/saved'
      : location.pathname.startsWith('/worker')
      ? '/worker'
      : location.pathname.startsWith('/client')
        ? '/client'
        : '/';
  const backLabel = applicationView ? 'All Applications' : savedView ? 'Saved Jobs' : 'Back';
  const backIcon = applicationView ? 'application' : savedView ? 'bookmark' : 'briefcase';

  useEffect(() => {
    if (job && job.id === id) {
      return;
    }

    const loadJob = async () => {
      try {
        setLoading(true);
        const data = await fetchJobById(id);
        setJob(data);
        setIsSaved(data?.isSaved || false);
        setHasApplied(data?.hasApplied || false);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadJob();
  }, [id, job]);

  if (loading) {
    return <div className="rounded-3xl bg-white p-8 text-center text-slate-600 shadow-sm">Loading job details...</div>;
  }

  if (error || !job) {
    return <div className="rounded-3xl bg-rose-50 p-8 text-center text-rose-700 shadow-sm">Job not found.</div>;
  }

  if (applicationView) {
    const isHired = ['hired', 'completed'].includes(applicationStatus);
    const statusTitle = isHired ? "You're Hired!" : applicationStatus === 'rejected' ? 'Not Selected' : 'Pending Review';
    const statusIcon = isHired ? 'check' : applicationStatus === 'rejected' ? 'x' : 'clock';
    const statusClass = isHired
      ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
      : applicationStatus === 'rejected'
        ? 'border-rose-200 bg-rose-50 text-rose-800'
        : 'border-amber-200 bg-amber-50 text-amber-800';
    const employerName = `${employer.firstName || ''} ${employer.lastName || ''}`.trim() || 'Employer';
    const contactNumber = employer.whatsapp || employer.phone || employer.secondaryPhone;
    const whatsappLink = contactNumber ? getWhatsAppLink(contactNumber, job, { messageType: 'hired' }) : null;
    const callLink = contactNumber ? getCallLink(contactNumber) : null;

    return (
      <section className="space-y-6">
        <Button variant="secondary" onClick={() => navigate(backPath)}><CardIcon name={backIcon} />{backLabel}</Button>

        <div className="space-y-6 rounded-3xl bg-white p-6 shadow-sm sm:p-8">
          <header className="border-b border-slate-200 pb-6">
            <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-bold uppercase ${statusClass}`}>
              <CardIcon name={statusIcon} />{statusTitle}
            </div>
            <h1 className="mt-5 text-3xl font-bold tracking-tight text-navy">{job.title}</h1>
            <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-slate-600"><CardIcon name="location" />{[job.area, job.city].filter(Boolean).join(', ') || 'Location unavailable'}</p>
            <p className="mt-5 text-sm leading-6 text-slate-700">
              {isHired
                ? 'Congratulations! The employer has accepted your application.'
                : applicationStatus === 'rejected'
                  ? 'The employer chose another applicant for this job.'
                  : 'Your application has been submitted and is currently being reviewed by the employer.'}
            </p>
            <p className="mt-4 text-sm font-medium text-slate-500">Applied on {formatDetailsDate(job.applicationCreatedAt)}</p>
          </header>

          <section>
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">About this job</h2>
            <p className="mt-3 text-base leading-7 text-slate-700">{job.description || 'No description provided.'}</p>
          </section>

          <section className="border-t border-slate-200 pt-6">
            <h2 className="text-lg font-bold text-navy">Job Information</h2>
            <dl className="mt-4 divide-y divide-slate-100 text-sm">
              <div className="flex justify-between gap-4 py-3"><dt className="inline-flex items-center gap-2"><CardIcon name="wrench" />Skill Required</dt><dd className="font-semibold text-slate-900">{job.skill?.name || 'General Builder'}</dd></div>
              <div className="flex justify-between gap-4 py-3"><dt className="inline-flex items-center gap-2"><CardIcon name="category" />Category</dt><dd className="text-right font-semibold text-slate-900">{job.skill?.category?.name || job.category || 'Unknown'}</dd></div>
              <div className="flex justify-between gap-4 py-3"><dt className="inline-flex items-center gap-2"><CardIcon name="people" />Workers Needed</dt><dd className="font-semibold text-slate-900">{job.requiredPeople ?? 1}</dd></div>
              <div className="flex justify-between gap-4 py-3"><dt className="inline-flex items-center gap-2"><CardIcon name="application" />Slots Remaining</dt><dd className="font-semibold text-slate-900">{job.slotsRemaining ?? 0}</dd></div>
              <div className="flex justify-between gap-4 py-3"><dt className="inline-flex items-center gap-2"><CardIcon name="calendar" />Expires</dt><dd className="font-semibold text-slate-900">{formatDetailsDate(job.expiresAt)}</dd></div>
            </dl>
          </section>

          <section className="border-t border-slate-200 pt-6">
            <h2 className="text-lg font-bold text-navy">Compensation</h2>
            <p className="mt-3 inline-flex items-center gap-2 text-2xl font-bold text-orange-600"><CardIcon name="money" />K{job.budget}</p>
            {job.isNegotiable && <p className="mt-1 text-sm text-slate-600">Negotiable</p>}
          </section>

          <section className="border-t border-slate-200 pt-6">
            <h2 className="text-lg font-bold text-navy">Employer</h2>
            <div className="mt-4 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-orange-400 to-orange-600 text-sm font-bold text-white">
                {`${employer.firstName?.[0] || ''}${employer.lastName?.[0] || ''}`.toUpperCase() || '?'}
              </div>
              <div>
                <p className="font-semibold text-slate-900">{employerName}</p>
                <p className="text-sm text-orange-600">View Profile →</p>
              </div>
            </div>
          </section>

          <section className="border-t border-slate-200 pt-6">
            <h2 className="text-lg font-bold text-navy">Job Activity</h2>
            <div className="mt-4 flex justify-between text-sm text-slate-700"><span className="inline-flex items-center gap-2"><CardIcon name="calendar" />Posted</span><span>{formatDetailsDate(job.createdAt)}</span></div>
          </section>

          <section className={`rounded-3xl border p-5 ${statusClass}`}>
            <h2 className="font-bold">{isHired ? "You're Hired!" : `${statusTitle}`}</h2>
            <p className="mt-2 text-sm leading-6">
              {isHired ? 'Contact the employer to confirm the work location, date and start time.' : 'Your application is with the employer. We\'ll notify you when a decision has been made.'}
            </p>
            {isHired && (
              <div className="mt-4 flex flex-wrap gap-3">
                {callLink && <a href={callLink} className="inline-flex rounded-2xl bg-navy px-4 py-3 text-sm font-semibold text-white">Call Employer</a>}
                {whatsappLink && <a href={whatsappLink} target="_blank" rel="noreferrer" className="inline-flex rounded-2xl border border-emerald-300 bg-white px-4 py-3 text-sm font-semibold text-emerald-700">WhatsApp</a>}
              </div>
            )}
          </section>
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-6">
      <Button variant="secondary" onClick={() => navigate(backPath)}>
        <CardIcon name={backIcon} />{backLabel}
      </Button>

      <div className="space-y-6 rounded-3xl bg-white p-8 shadow-sm">
        <div className="border-b border-slate-200 pb-6">
          <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-3">
                <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-bold ${job.urgency === 'urgent' ? 'border-red-200 bg-red-50 text-red-700' : job.urgency === 'high' ? 'border-orange-200 bg-orange-50 text-orange-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
                  <CardIcon name={job.urgency === 'urgent' || job.urgency === 'high' ? 'flame' : 'check'} />{job.urgency?.toUpperCase() || 'NORMAL'}
                </span>
                <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700">
                  <CardIcon name="clock" />{job.workType?.replace('_', ' ') || 'Full Time'}
                </span>
              </div>
              <p className="mt-5 text-sm font-medium text-slate-500">{job.postedAgo || formatPostedAt(job.createdAt)}</p>
              <h1 className="mt-2 text-3xl font-bold tracking-tight text-navy sm:text-4xl">{job.title}</h1>
            </div>

            <div className="shrink-0 rounded-3xl border border-orange-200 bg-orange-50 p-5 xl:min-w-52">
              <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-orange-700"><CardIcon name="money" />Budget</p>
              <p className="mt-2 text-2xl font-bold text-orange-700">K{job.budget}</p>
              {job.isNegotiable && <p className="mt-1 text-sm text-orange-700">Negotiable</p>}
            </div>
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6">
          <h2 className="inline-flex items-center gap-2 text-lg font-semibold text-navy"><CardIcon name="application" />Description</h2>
          <p className="mt-3 text-slate-700 leading-7">{job.description}</p>
        </div>

        <div className="grid gap-4 xl:grid-cols-[1fr_320px]">
          <div className="space-y-4">
            <div className="rounded-3xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-navy">Job Information</h3>
              <ul className="mt-4 space-y-3 text-sm text-slate-700">
                <li className="flex justify-between gap-4"><span className="inline-flex items-center gap-2"><CardIcon name="wrench" />Skill Required</span><span className="font-semibold text-slate-900">{job.skill?.name || 'General Builder'}</span></li>
                <li className="flex justify-between gap-4"><span className="inline-flex items-center gap-2"><CardIcon name="category" />Category</span><span className="font-semibold text-slate-900">{job.skill?.category?.name || job.category || 'Unknown'}</span></li>
                <li className="flex justify-between gap-4"><span className="inline-flex items-center gap-2"><CardIcon name="people" />Workers Needed</span><span className="font-semibold text-slate-900">{job.requiredPeople ?? 1}</span></li>
                <li className="flex justify-between gap-4"><span className="inline-flex items-center gap-2"><CardIcon name="application" />Slots Remaining</span><span className="font-semibold text-slate-900">{job.slotsRemaining ?? 0}</span></li>
                <li className="flex justify-between gap-4"><span className="inline-flex items-center gap-2"><CardIcon name="calendar" />Expires</span><span className="font-semibold text-slate-900">{job.expiresAt ? new Date(job.expiresAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}</span></li>
              </ul>
            </div>

            <div className="rounded-3xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-navy">Job Activity</h3>
              <div className="mt-4 space-y-3 text-sm text-slate-700">
                <div className="flex justify-between"><span className="inline-flex items-center gap-2"><CardIcon name="calendar" />Posted</span><span>{job.createdAt ? new Date(job.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}</span></div>
                <div className="flex justify-between"><span className="inline-flex items-center gap-2"><CardIcon name="eye" />Applications</span><span>{job.applicationsCount ?? 0}</span></div>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-navy">Employer</h3>
            <div className="mt-4 flex items-center gap-4">
              <div className="h-14 w-14 rounded-full overflow-hidden bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white text-base font-bold">
                {hasProfilePhoto ? (
                  <img
                    src={profilePhoto}
                    alt="Employer avatar"
                    className="h-full w-full object-cover"
                    onError={() => setAvatarVisible(false)}
                  />
                ) : (`${employer.firstName?.[0] || ''}${employer.lastName?.[0] || ''}`.toUpperCase())}
              </div>
              <div>
                <p className="text-base font-semibold text-slate-900">{employer.firstName} {employer.lastName}</p>
                <p className="text-sm text-slate-500">{employer.area ? `${employer.area}, ` : ''}{employer.city}</p>
              </div>
            </div>
              <div className="mt-6">
              <span className="text-sm font-semibold text-orange-600">View Profile →</span>
            </div>
          </div>
        </div>

        {!isClientView && <div className="flex flex-col gap-3 sm:flex-row">
          <Button variant={isSaved ? 'secondary' : 'ghost'} onClick={async () => {
            if (!auth?.token) {
              navigate('/login');
              return;
            }
            try {
              await (isSaved ? unsaveJob(job.id) : saveJob(job.id));
              setIsSaved(!isSaved);
            } catch (err) {
              console.error(err);
            }
          }}>
            {isSaved ? 'Saved' : 'Save Job'}
          </Button>
          <Button variant="primary" onClick={async () => {
            if (!auth?.token) {
              navigate('/login');
              return;
            }
            if (!hasApplied) {
              try {
                await applyJob(job.id);
                setHasApplied(true);
              } catch (err) {
                console.error(err);
              }
            }
          }} disabled={hasApplied}>
            {hasApplied ? '✓ Applied' : 'Apply Now'}
          </Button>
        </div>}
      </div>
    </section>
  );
}

export default JobDetailsPage;